// Copyright (c) 2026 Endel Dreyer.
//
// This software is released under the MIT License.
// https://opensource.org/license/MIT
//
// colyseus.js@0.17.34
import { H3TransportTransport } from './transport/H3Transport.mjs';
import { WebSocketTransport } from './transport/WebSocketTransport.mjs';
import { CloseCode } from '@colyseus/shared-types';

const onOfflineListeners = [];
const hasGlobalEventListeners = typeof (addEventListener) === "function" && typeof (removeEventListener) === "function";
if (hasGlobalEventListeners) {
    /**
     * Detects when the network is offline and closes all connections.
     * (When switching wifi networks, etc.)
     */
    addEventListener("offline", () => {
        console.warn(`@colyseus/sdk: 🛑 Network offline. Closing ${onOfflineListeners.length} connection(s)`);
        onOfflineListeners.forEach((listener) => listener());
    }, false);
}
class Connection {
    transport;
    events = {};
    url;
    options;
    #_offlineListener = (hasGlobalEventListeners) ? () => this.close(CloseCode.MAY_TRY_RECONNECT) : null;
    constructor(protocol) {
        switch (protocol) {
            case "h3":
                this.transport = new H3TransportTransport(this.events);
                break;
            default:
                this.transport = new WebSocketTransport(this.events);
                break;
        }
    }
    connect(url, options) {
        if (hasGlobalEventListeners) {
            const onOpen = this.events.onopen;
            this.events.onopen = (ev) => {
                onOfflineListeners.push(this.#_offlineListener);
                onOpen?.(ev);
            };
            const onClose = this.events.onclose;
            this.events.onclose = (ev) => {
                onOfflineListeners.splice(onOfflineListeners.indexOf(this.#_offlineListener), 1);
                onClose?.(ev);
            };
        }
        this.url = url;
        this.options = options;
        this.transport.connect(url, options);
    }
    send(data) {
        this.transport.send(data);
    }
    sendUnreliable(data) {
        this.transport.sendUnreliable(data);
    }
    reconnect(queryParams) {
        const url = new URL(this.url);
        // override query params
        for (const key in queryParams) {
            url.searchParams.set(key, queryParams[key]);
        }
        this.transport.connect(url.toString(), this.options);
    }
    close(code, reason) {
        this.transport.close(code, reason);
    }
    get isOpen() {
        return this.transport.isOpen;
    }
}

export { Connection };
//# sourceMappingURL=Connection.mjs.map
